import { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Stack,
  Tabs,
  Group,
} from "@mantine/core";
import { useForm } from "react-hook-form";
import resolver from "@congrs/resolver";
import {
  useCreateTranslations,
  useUpdateTranslation,
  useGetTranslationsByKey,
} from "../../api/translations";
import { web_createTranslationSchema } from "@congrs/schemas";

function TranslationModal({
  opened,
  onClose,
  translationKey = null,
  mode = "create",
  validLanguages = [],
}) {
  const isEditMode = mode === "edit" && translationKey;
  const [originalValues, setOriginalValues] = useState({});

  const { data: existingTranslations, isLoading } = useGetTranslationsByKey(
    isEditMode ? translationKey : null
  );

  const { mutate: createTranslations, isPending: isCreating } =
    useCreateTranslations();
  const { mutate: updateTranslations, isPending: isUpdating } =
    useUpdateTranslation();

  const defaultValues = {
    translationKey: "",
    ...validLanguages.reduce((acc, lang) => {
      acc[lang] = "";
      return acc;
    }, {}),
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: resolver(web_createTranslationSchema(validLanguages)),
  });

  useEffect(() => {
    if (isEditMode && existingTranslations) {
      const translationsArray = Array.isArray(existingTranslations)
        ? existingTranslations
        : existingTranslations.data || [];

      const translations = {};
      translationsArray.forEach((t) => {
        translations[t.languageCode] = t.value;
      });

      const values = {
        translationKey: translationKey,
        ...validLanguages.reduce((acc, lang) => {
          acc[lang] = translations[lang] || "";
          return acc;
        }, {}),
      };

      setValue("translationKey", values.translationKey);
      validLanguages.forEach((lang) => {
        setValue(lang, values[lang]);
      });

      setOriginalValues(values);
    }
  }, [existingTranslations, isEditMode, translationKey, setValue]);

  const onSubmit = (values) => {
    if (isEditMode) {
      const translationsArray = Array.isArray(existingTranslations)
        ? existingTranslations
        : existingTranslations?.data || [];

      const updates = [];
      const translationsToCreate = [];

      validLanguages.forEach((lang) => {
        const existing = translationsArray.find((t) => t.languageCode === lang);
        const newValue = values[lang];
        const oldValue = originalValues[lang];

        if (existing && newValue !== oldValue && newValue) {
          updates.push({
            id: existing.id,
            value: newValue,
          });
        } else if (!existing && newValue) {
          translationsToCreate.push({
            languageCode: lang,
            value: newValue,
          });
        }
      });

      if (updates.length > 0) {
        updateTranslations({ updates });
      }

      if (translationsToCreate.length > 0) {
        createTranslations({
          translationKey: values.translationKey,
          translations: translationsToCreate,
        });
      }
    } else {
      const translations = validLanguages
        .filter((lang) => values[lang])
        .map((lang) => ({
          languageCode: lang,
          value: values[lang],
        }));

      if (translations.length > 0) {
        createTranslations({
          translationKey: values.translationKey,
          translations: translations,
        });
      }
    }

    reset();
    setOriginalValues({});
    onClose();
  };

  const handleClose = () => {
    reset();
    setOriginalValues({});
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? "Edit Translation" : "Add Translation"}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <TextInput
            {...register("translationKey")}
            label="Translation Key"
            placeholder="e.g., welcome_message"
            withAsterisk
            disabled={isEditMode}
            error={errors.translationKey?.message}
          />

          <Tabs defaultValue={validLanguages[0]}>
            <Tabs.List>
              {validLanguages.map((lang) => (
                <Tabs.Tab key={lang} value={lang}>
                  {lang.toUpperCase()}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {validLanguages.map((lang) => (
              <Tabs.Panel key={lang} value={lang} pt="md">
                <Textarea
                  {...register(lang)}
                  placeholder={`Enter ${lang.toUpperCase()} translation`}
                  minRows={3}
                  error={errors[lang]?.message}
                />
              </Tabs.Panel>
            ))}
          </Tabs>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isCreating || isUpdating || isLoading}
            >
              {isEditMode ? "Update" : "Create"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default TranslationModal;
